import { create } from 'danmu';

/**
 * ArtPlayer 弹幕插件
 * @param {Object} options - 插件配置选项
 * @returns {Function} ArtPlayer 插件函数
 */
export default function artplayerPluginDanmaku(options = {}) {
    return (art) => {
        const {
            // 弹幕配置（danmu 官方参数）
            trackHeight = 60,           // 轨道高度（像素或百分比表达式，如 '100% / 6'）
            trackCount = null,          // 轨道数量（如果设置，会自动计算 trackHeight = '100% / trackCount'）
            trackArea = null,           // 轨道渲染区域 { x?: {start, end}, y?: {start, end} }
            durationRange = [20000, 23000], // 弹幕运动时间范围 [最小, 最大]（时间越大，速度越慢）
            interval = 500,             // 轮询间隔（毫秒）
            gap = 8,                   // 弹幕间距（像素）
            mode = 'strict',            // 模式：none | strict | adaptive
            direction = 'right',        // 方向：right | left
            limits = { view: 200, stash: 120 }, // 显示和缓存限制
            // 样式配置
            fontSize = '16px',          // 默认字号
            defaultColor = '#ffffff',   // 默认颜色
            opacity = 1,                // 透明度
            // 容器配置
            containerStyle = {},        // 自定义容器样式
            // 控制按钮配置
            showToggleButton = true,    // 显示开关按钮
            showSendButton = true,      // 显示发送按钮
            showActionButtons = false,   // 显示交互按钮（点赞、踩、举报）
            lockTime = 5,               // 发送按钮锁定时间（秒），范围 [1 ~ 60]
            // 回调函数
            onSend = null,              // 发送弹幕回调
            onLoad = null,              // 加载弹幕回调
            debug = false,              // 🔑 调试模式（显示详细日志）
            onLike = null,              // 点赞回调 (danmakuData, isLiked) => {}
            onDislike = null,           // 踩回调 (danmakuData, isDisliked) => {}
            onReport = null,            // 举报回调 (danmakuData) => {}
        } = options;

        let danmakuManager = null;
        let danmakuContainer = null;
        let danmakuVisible = true;
        let danmakuQueue = [];  // 🔑 弹幕队列（按时间排序）
        let schedulerTimer = null;  // 弹幕调度器定时器
        let currentDanmakuIndex = 0;  // 🔑 当前弹幕游标（指向下一个要显示的弹幕）
        let isSchedulerRunning = false;  // 🔑 调度器是否正在运行
        let isSeeking = false;  // 🔑 是否正在快进快退
        let lastSeekTime = 0;  // 🔑 记录上次播放时间，用于判断快进还是快退

        // 创建交互按钮的辅助函数
        function createActionButton(type, svgHTML, defaultColor, activeColor) {
            const button = document.createElement('button');
            button.className = `danmaku-action-btn danmaku-${type}-btn`;
            button.innerHTML = svgHTML;
            button.style.cssText = `
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 2px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: ${defaultColor};
                transition: all 0.2s;
                pointer-events: auto;
            `;
            button.dataset.defaultColor = defaultColor;
            button.dataset.activeColor = activeColor;
            button.dataset.active = 'false';

            // 悬停效果
            button.addEventListener('mouseenter', () => {
                if (button.dataset.active !== 'true') {
                    button.style.color = activeColor;
                    button.style.transform = 'scale(1.1)';
                }
            });

            button.addEventListener('mouseleave', () => {
                if (button.dataset.active !== 'true') {
                    button.style.color = defaultColor;
                    button.style.transform = 'scale(1)';
                }
            });

            return button;
        }

        // 更新按钮颜色的辅助函数
        function updateButtonColor(button, isActive) {
            button.dataset.active = isActive ? 'true' : 'false';
            button.style.color = isActive ? button.dataset.activeColor : button.dataset.defaultColor;
            button.style.transform = isActive ? 'scale(1.1)' : 'scale(1)';
        }

        // 初始化弹幕系统
        function init() {
            try {
                // 绑定播放器事件
                bindPlayerEvents();

                // 添加控制按钮
                if (showToggleButton) {
                    addToggleControl();
                }
                if (showSendButton) {
                    addSendControl();
                }
                
                // 🔑 提前执行加载回调（在 ready 之前），让用户可以预加载弹幕数据
                if (onLoad && typeof onLoad === 'function') {
                    const pluginAPI = {
                        send,
                        load,
                        clear,
                        toggle,
                        setOpacity,
                        destroy,
                        get manager() {
                            return danmakuManager;  // 初始为 null，ready 后才有值
                        },
                        get container() {
                            return danmakuContainer;  // 初始为 null，ready 后才有值
                        },
                    };
                    console.log('[弹幕插件] ⏰ 提前触发 onLoad 回调，可以预加载弹幕数据');
                    onLoad(pluginAPI);
                }
           
                // 等待 ArtPlayer 完全初始化后再查找弹幕层
                if (art.isReady) {
                    // ArtPlayer 已经准备好了，直接初始化弹幕
                    initDanmakuContainer();
                } else {
                    // 等待 ready 事件
                    art.on('ready', () => {
                        initDanmakuContainer();
                    });
                }

                console.log('[弹幕插件] ✓ 插件初始化完成，等待 ArtPlayer ready 事件');
                return true;
            } catch (error) {
                console.error('[弹幕插件] 初始化失败:', error);
                return false;
            }
        }

        // 初始化弹幕容器和弹幕管理器
        function initDanmakuContainer() {
            try {
                // 尝试使用 ArtPlayer 自带的弹幕层（现在应该已经创建了）
                danmakuContainer = art.template.$danmuku || art.query('.art-danmuku');

                // 如果不存在，则创建自己的弹幕容器
                if (!danmakuContainer) {
                    console.log('[弹幕插件] ArtPlayer 没有弹幕层，创建自定义弹幕容器...');

                    // 创建弹幕容器
                    danmakuContainer = document.createElement('div');
                    danmakuContainer.className = 'artplayer-plugin-danmaku-container';
                    danmakuContainer.style.cssText = `
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        pointer-events: none;
                        overflow: visible;
                        z-index: 10;
                    `;

                    // 将弹幕容器添加到播放器中
                    const playerContainer = art.template.$player;
                    if (playerContainer) {
                        playerContainer.appendChild(danmakuContainer);
                        console.log('[弹幕插件] ✓ 自定义弹幕容器已创建并添加到播放器');
                    } else {
                        throw new Error('无法找到播放器容器');
                    }
                } else {
                    console.log('[弹幕插件] ✓ 使用 ArtPlayer 自带的弹幕层');
                }
                

                // 处理轨道配置
                let finalTrackHeight = trackHeight;
                let finalTrackArea = trackArea;
                
                // 如果设置了 trackCount，自动计算 trackHeight
                if (trackCount && typeof trackCount === 'number') {
                    finalTrackHeight = `100% / ${trackCount}`;
                    console.log(`[弹幕插件] 根据 trackCount=${trackCount} 计算 trackHeight:`, finalTrackHeight);
                    
                    // 如果没有设置 trackArea，根据 trackCount 自动设置
                    if (!trackArea) {
                        const calculatedHeight = Math.floor(danmakuContainer.offsetHeight / trackCount) * trackCount;
                        finalTrackArea = {
                            y: {
                                start: 0,
                                end: calculatedHeight,
                            }
                        };
                        console.log(`[弹幕插件] 自动计算渲染区域:`, finalTrackArea);
                    }
                }
                
                // 创建弹幕管理器（使用官方正确的参数）
                console.log('[弹幕插件] 创建弹幕管理器配置:', {
                    trackHeight: finalTrackHeight,
                    durationRange,
                    trackArea: finalTrackArea
                });
                
                danmakuManager = create({
                    trackHeight: finalTrackHeight,
                    durationRange: durationRange,   // 弹幕运动时间范围
                    interval: interval,             // 轮询间隔
                    direction: direction,           // 方向
                    mode: mode,                     // 碰撞检测模式
                    gap: gap,                       // 弹幕间距
                    rate: 1,                        // 播放速率
                    limits: limits,                 // 显示和缓存限制
                    plugin: {
                        // 创建弹幕节点
                        $createNode(danmaku) {
                            const data = danmaku.data;
                            
                            // 🔑 关键：正确获取文本内容
                            // danmu 库的 data 可能是：
                            // 1. 字符串: "hello"
                            // 2. 对象: { text: "hello", color: "#fff", ... }
                            let text;
                            if (typeof data === 'string') {
                                text = data;
                            } else if (typeof data === 'object' && data !== null) {
                                text = data.text || String(data);
                            } else {
                                text = String(data);
                            }

                            // 使用 danmu 库提供的 node，设置其样式
                            Object.assign(danmaku.node.style, {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                            });

                            // 创建文本节点
                            const textNode = document.createElement('span');
                            textNode.textContent = text;
                            
                            // 获取颜色配置（支持纯色和渐变）
                            let colorValue = defaultColor;
                            if (typeof data === 'object' && data !== null) {
                                colorValue = data.color || defaultColor;
                            }
                            
                            const isGradient = typeof colorValue === 'string' && colorValue.includes('linear-gradient');
                            
                            // 获取字体大小
                            let fontSizeValue = fontSize;
                            if (typeof data === 'object' && data !== null && data.fontSize) {
                                fontSizeValue = data.fontSize;
                            }
                            
                            if (isGradient) {
                                // 渐变色弹幕 - 使用更轻的阴影
                                textNode.style.cssText = `
                                    background: ${colorValue};
                                    -webkit-background-clip: text;
                                    -webkit-text-fill-color: transparent;
                                    background-clip: text;
                                    font-size: ${fontSizeValue};
                                    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6))
                                           drop-shadow(0 0 2px rgba(0,0,0,0.4));
                                    font-weight: bold;
                                    padding: 2px 8px;
                                    white-space: nowrap;
                                `;
                            } else {
                                // 纯色弹幕
                                textNode.style.cssText = `
                                    color: ${colorValue};
                                    font-size: ${fontSizeValue};
                                    text-shadow: 
                                        -1px -1px 0 rgba(0,0,0,0.8),
                                        1px -1px 0 rgba(0,0,0,0.8),
                                        -1px 1px 0 rgba(0,0,0,0.8),
                                        1px 1px 0 rgba(0,0,0,0.8),
                                        0 0 3px rgba(0,0,0,0.8);
                                    font-weight: bold;
                                    padding: 2px 8px;
                                    white-space: nowrap;
                                `;
                            }

                            // 添加文本节点到 danmu.node
                            danmaku.node.appendChild(textNode);

                            // 如果启用交互按钮，添加按钮容器
                            if (showActionButtons) {
                                const buttonsContainer = document.createElement('div');
                                buttonsContainer.style.cssText = `
                                    display: none;
                                    align-items: center;
                                    gap: 4px;
                                    opacity: 1;
                                    transition: opacity 0.2s;
                                    flex-shrink: 0;
                                `;

                                // 点赞状态
                                let isLiked = false;
                                let isDisliked = false;

                                // 创建点赞按钮
                                const likeBtn = createActionButton(
                                    'like',
                                    '<svg t="1767862551928" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4772" width="20" height="20"><path d="M844.231111 391.39555600000006H659.9111109999999c-13.653333-2.275556-18.204444-15.928889-20.48-22.755556V202.52444400000002c0-45.511111-36.408889-81.92-81.92-81.92-40.96 0-75.093333 31.857778-81.92 70.542223-25.031111 157.013333-120.604444 209.351111-182.044444 225.28 2.275556 6.826667 2.275556 11.377778 2.275555 15.928889v441.457777c0 9.102222-2.275556 18.204444-6.826666 27.306666999999997h446.008888c43.235556-9.102222 72.817778-25.031111 93.297778-68.266667l93.297778-327.68c18.204444-59.16444400000001-11.377778-116.053333-77.368889-113.77777699999999zM257.137778 876.0888889999999V432.355556c0-13.653333-18.204444-25.031111-38.684445-25.031112H157.013333c-31.857778 0-59.16444400000001 27.306666999999997-59.16444400000001 59.16444500000001v373.191111c0 34.133333 25.031111 61.44 59.16444400000001 61.44h61.44c20.48 0 38.684444-11.377778 38.684445-25.031111z m0 0" fill="#e6e6e6" p-id="4773"></path></svg>',
                                    '#ffffff',
                                    '#4CAF50'
                                );
                                
                                // 创建踩按钮
                                const dislikeBtn = createActionButton(
                                    'dislike',
                                    '<svg t="1767862630923" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6736" width="20" height="20"><path d="M322.4 107.2v529.4c67.7 24.7 120.3 96 139.7 203.4 8.1 45.7 48.6 76.9 95.3 76.9 52.7 0 95.3-43.7 95.3-97.7V668.6h184.5c66.9 0 115.5-64.5 97.3-131l-91.4-338.9c-14.1-54-62.8-91.5-117.5-91.5H322.4z m-51.2 0H166.1c-44.6 0-81.1 37.5-81.1 83.2v351.4c0 45.7 36.5 85.2 81.1 85.2H271.2V107.2z" p-id="6737" fill="#e6e6e6"></path></svg>',
                                    '#ffffff',
                                    '#FF5722'
                                );

                                // 创建举报按钮
                                const reportBtn = createActionButton(
                                    'report',
                                    '<svg t="1767862736218" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="9553" width="20" height="20"><path d="M641.57184 181.12c-43.27936-17.87904-76.28288-46.9248-140.14464-45.98272-20.48512 1.61792-30.48448 4.92032-45.09184 10.74688-96 37.56032-185.74848 95.50848-307.4816 117.76 0 378.93632 182.76864 516.69504 365.46048 625.2544 214.016-106.14784 362.25024-325.61664 360.81152-625.2544-87.80288-14.3616-162.24768-53.07904-233.55392-82.52416z m-90.54208 499.11296H472.96512v-58.39872h78.06464v58.39872z m0-133.3248H472.96512V312.5504h78.06464v234.35776z" p-id="9554" fill="#e6e6e6"></path></svg>',
                                    '#ffffff',
                                    '#F44336'
                                );

                                // 点赞按钮点击事件
                                likeBtn.addEventListener('click', (e) => {
                                    e.stopPropagation();
                                    isLiked = !isLiked;
                                    if (isLiked && isDisliked) {
                                        isDisliked = false;
                                        updateButtonColor(dislikeBtn, false);
                                    }
                                    updateButtonColor(likeBtn, isLiked);
                                    
                                    if (onLike && typeof onLike === 'function') {
                                        onLike(data, isLiked);
                                    }
                                });

                                // 踩按钮点击事件
                                dislikeBtn.addEventListener('click', (e) => {
                                    e.stopPropagation();
                                    isDisliked = !isDisliked;
                                    if (isDisliked && isLiked) {
                                        isLiked = false;
                                        updateButtonColor(likeBtn, false);
                                    }
                                    updateButtonColor(dislikeBtn, isDisliked);
                                    
                                    if (onDislike && typeof onDislike === 'function') {
                                        onDislike(data, isDisliked);
                                    }
                                });

                                // 举报按钮点击事件
                                reportBtn.addEventListener('click', (e) => {
                                    e.stopPropagation();
                                    updateButtonColor(reportBtn, true);
                                    
                                    if (onReport && typeof onReport === 'function') {
                                        onReport(data);
                                    }
                                });

                                // 添加按钮到容器
                                buttonsContainer.appendChild(likeBtn);
                                buttonsContainer.appendChild(dislikeBtn);
                                buttonsContainer.appendChild(reportBtn);
                                danmaku.node.appendChild(buttonsContainer);

                                // 鼠标悬停事件
                                danmaku.node.addEventListener('mouseenter', () => {
                                    // 暂停当前弹幕（danmu 库会自动管理暂停状态，不会移除暂停的弹幕）
                                    danmaku.pause();
                                    buttonsContainer.style.display = 'flex';
                                    setTimeout(() => {
                                        buttonsContainer.style.opacity = '1';
                                    }, 10);
                                });

                                danmaku.node.addEventListener('mouseleave', () => {
                                    // 恢复当前弹幕（检查管理器是否被冻结）
                                    if (!danmakuManager.isFreeze()) {
                                        danmaku.resume();
                                    }
                                    buttonsContainer.style.opacity = '0';
                                    setTimeout(() => {
                                        if (buttonsContainer.style.opacity === '0') {
                                            buttonsContainer.style.display = 'none';
                                        }
                                    }, 200);
                                });
                            }
                        },
                        
                        // 弹幕过滤钩子
                        willRender(ref) {
                            return ref;
                        },
                    },
                });

                // 挂载并启动
                danmakuManager.mount(danmakuContainer);
                
                // 设置轨道高度（如果使用了 trackCount 或自定义 trackHeight）
                if (finalTrackHeight !== trackHeight || trackCount) {
                    danmakuManager.setTrackHeight(finalTrackHeight);
                    console.log(`[弹幕插件] ✓ 设置轨道高度:`, finalTrackHeight);
                }
                
                // 设置渲染区域（如果配置了 trackArea）
                if (finalTrackArea) {
                    danmakuManager.setArea(finalTrackArea);
                    //console.log(`[弹幕插件] ✓ 设置渲染区域:`, finalTrackArea);
                }
                
                // 设置持续时间范围
                if (typeof danmakuManager.setDurationRange === 'function') {
                    danmakuManager.setDurationRange(durationRange);
                    //console.log(`[弹幕插件] ✓ 使用 setDurationRange 设置持续时间:`, durationRange);
                } else if (typeof danmakuManager.setTimes === 'function') {
                    danmakuManager.setTimes(durationRange);
                    //console.log(`[弹幕插件] ✓ 使用 setTimes 设置持续时间:`, durationRange);
                }
 
                // 同步初始播放速率
                const initialPlaybackRate = art.playbackRate || 1;
                danmakuManager.setRate(initialPlaybackRate);
                //console.log(`[弹幕插件] 初始播放速率: ${initialPlaybackRate}x`);

                // 检查视频是否正在播放
                if (art.playing) {
                    // 视频正在播放（自动播放），启动弹幕
                    danmakuManager.startPlaying();
                    console.log(`[弹幕插件] 检测到视频正在播放，弹幕已启动`);
                } else {
                    // 视频未播放，冻结弹幕，等待播放
                    danmakuManager.freeze();
                    console.log(`[弹幕插件] 初始化完成，弹幕已冻结，等待播放`);
                }
                
                // 🔑 如果队列中已有数据（提前加载的），初始化游标
                if (danmakuQueue.length > 0 && art.currentTime > 0) {
                    currentDanmakuIndex = findDanmakuIndexByTime(art.currentTime);
                    //console.log(`[弹幕调度器] 检测到预加载数据，初始游标: ${currentDanmakuIndex}（当前时间: ${art.currentTime.toFixed(2)}s）`);
                }

                //console.log('[弹幕插件] ✓ 弹幕容器和弹幕管理器初始化成功');
            } catch (error) {
                console.error('[弹幕插件] 弹幕容器初始化失败:', error);
            }
        }

        // 🔑 二分查找：找到大于等于指定时间的第一个弹幕索引
        function findDanmakuIndexByTime(targetTime) {
            let left = 0;
            let right = danmakuQueue.length;
            
            while (left < right) {
                const mid = Math.floor((left + right) / 2);
                if (danmakuQueue[mid].time < targetTime) {
                    left = mid + 1;
                } else {
                    right = mid;
                }
            }
            
            return left;
        }

        // 绑定播放器事件
        function bindPlayerEvents() {
            // 🔑 在播放过程中持续更新时间（用于准确判断快进快退）
            art.on('video:timeupdate', () => {
                if (!isSeeking && art.playing) {
                    lastSeekTime = art.currentTime;
                }
            });

            // 监听 seeking 事件（快进快退开始时）
            art.on('video:seeking', () => {
                if (danmakuManager) {
                    isSeeking = true;
                    danmakuManager.freeze();  // 🔑 冻结弹幕
                    stopScheduler();  // 🔑 停止调度器
                    //console.log('[弹幕插件] ⏭️ 视频正在跳转，弹幕已冻结');
                }
            });

            // 监听 seeked 事件（快进快退完成，视频可能还在loading）
            art.on('video:seeked', () => {
                if (danmakuManager) {
                    const currentTime = art.currentTime;
                    const timeDiff = currentTime - lastSeekTime;
                    
                    // 🔑 只有时间差超过1秒才处理，避免误触发
                    if (Math.abs(timeDiff) > 1) {
                        const isForward = timeDiff > 0;
                        
                        //console.log(`[弹幕插件] ⏩ 视频跳转: ${lastSeekTime.toFixed(2)}s → ${currentTime.toFixed(2)}s (${isForward ? '快进' : '快退'}, 跳过${Math.abs(timeDiff).toFixed(1)}秒)`);
                        
                        // 🔑 游标法：直接移动游标到新位置（二分查找）
                        const oldIndex = currentDanmakuIndex;
                        currentDanmakuIndex = findDanmakuIndexByTime(currentTime);
                        
                        //console.log(`[弹幕调度器] 游标移动: ${oldIndex} → ${currentDanmakuIndex}，剩余 ${danmakuQueue.length - currentDanmakuIndex} 条弹幕`);
                        
                        lastSeekTime = currentTime;
                    } else {
                        // 时间差太小，不是真正的跳转，忽略
                        //console.log(`[弹幕插件] ⏩ 视频微调: ${lastSeekTime.toFixed(2)}s → ${currentTime.toFixed(2)}s (忽略)`);
                    }
                    
                    isSeeking = false;
                }
            });

            // 监听 waiting 事件（视频缓冲中）
            art.on('video:waiting', () => {
                if (danmakuManager && !isSeeking) {
                    danmakuManager.freeze();  // 🔑 冻结弹幕
                    stopScheduler();
                }
            });

            // 监听 playing 事件（视频真正开始播放，不再 loading）
            art.on('video:playing', () => {
                if (danmakuManager && !isSchedulerRunning) {
                    danmakuManager.unfreeze();  // 🔑 解冻弹幕
                    if (danmakuVisible) {
                        danmakuManager.startPlaying();
                    }
                    startScheduler();  // 🔑 启动调度器
                }
            });

            // 播放时解冻弹幕并启动调度器（兜底）
            art.on('play', () => {
                if (danmakuManager && !isSchedulerRunning) {
                    danmakuManager.unfreeze();  // 解冻弹幕
                    if (danmakuVisible) {
                        danmakuManager.startPlaying();  // 启动弹幕轮询
                    }
                    startScheduler();  // 🔑 启动弹幕调度器
                }
            });

            // 暂停时冻结弹幕并停止调度器
            art.on('pause', () => {
                if (danmakuManager) {
                    danmakuManager.freeze();  // 冻结弹幕，所有弹幕停在当前位置
                    stopScheduler();  // 🔑 停止弹幕调度器
                }
            });

            // 监听视频播放速率变化，同步弹幕速率
            art.on('video:ratechange', () => {
                if (danmakuManager) {
                    const playbackRate = art.playbackRate || 1;
                    danmakuManager.setRate(playbackRate);
                }
            });

            // 销毁时清理
            art.on('destroy', () => {
                destroy();
            });
        }

        // 🔑 启动弹幕调度器
        function startScheduler() {
            // 如果已经在运行，不重复启动
            if (isSchedulerRunning) {
                return;
            }
            
            stopScheduler();  // 先停止旧的调度器（双重保险）
            
            if (danmakuQueue.length === 0) {
                return;
            }
            
            // 每 500ms 检查一次是否有弹幕需要显示
            schedulerTimer = setInterval(() => {
                if (!art.playing || !danmakuVisible) return;
                
                const currentTime = art.currentTime;  // 当前视频播放时间（秒）
                let displayCount = 0;  // 🔑 本次显示的弹幕数量
                
                // 🔑 游标法：从当前游标开始，顺序显示时间已到的弹幕
                while (currentDanmakuIndex < danmakuQueue.length) {
                    const danmaku = danmakuQueue[currentDanmakuIndex];
                    
                    if (danmaku.time <= currentTime) {
                        // 显示弹幕
                        const displayData = {
                            text: danmaku.text,
                            color: danmaku.color,
                            fontSize: danmaku.fontSize
                        };
                        danmakuManager.unshift(displayData);
                        
                        // 🔑 游标前进
                        currentDanmakuIndex++;
                        displayCount++;
                        
                        // 🔑 调试模式：输出详细日志
                        // if (debug) {
                        //     console.log(`[弹幕调度器] 显示弹幕 #${currentDanmakuIndex - 1}: ${danmaku.time}s - ${danmaku.text}`);
                        // }
                    } else {
                        // 后面的弹幕时间还没到，停止
                        break;
                    }
                }
                
                // 🔑 批量日志：显示本次处理结果
                if (displayCount > 0 && debug) {
                    //console.log(`[弹幕调度器] 本次显示 ${displayCount} 条弹幕，当前时间: ${currentTime.toFixed(2)}s，游标: ${currentDanmakuIndex}/${danmakuQueue.length}`);
                }
            }, 500);  // 200ms 检查一次
            
            isSchedulerRunning = true;
        }

        // 🔑 停止弹幕调度器
        function stopScheduler() {
            if (schedulerTimer) {
                clearInterval(schedulerTimer);
                schedulerTimer = null;
                isSchedulerRunning = false;
            }
        }

        // 添加弹幕开关控制按钮
        // 添加弹幕开关控制按钮
        function addToggleControl() {
            // SVG 图标定义
            const danmakuOnIcon = `<svg width="28" height="28" viewBox="-2,-2,28,28">
            <path d="M15.3468 7C15.6522 7 15.905 7.22505 15.9484 7.51835L15.955 7.60823L15.9549 8.01653L16.2911 8.01726C16.5965 8.01726 16.8493 8.24231 16.8928 
            8.5356L16.8994 8.62548V12.463C16.8994 12.7989 16.6271 13.0713 16.2911 13.0713L14.4101 13.0707V13.7582L16.2047 13.7588C16.5406 13.7588 16.8129 14.0311 
            16.8129 14.367C16.8129 14.6724 16.5878 14.9252 16.2946 14.9687L16.2047 14.9753L14.4101 14.9748L14.4104 15.9871C14.4104 16.323 14.1381 16.5953 13.8022 16.5953C13.4968 16.5953 13.244 16.3703 13.2006 16.077L13.194 15.9871L13.1935 14.9748L11.3133 14.9753C10.9773 14.9753 10.705 14.703 10.705 14.367C10.705  
            14.0617 10.9301 13.8089 11.2234 13.7654L11.3133 13.7588L13.1935 13.7582V13.0707L11.3133 13.0713C11.0079 13.0713 10.7551 12.8462 10.7116 12.5529L10.705 12.463V8.62548C10.705 8.28957 10.9773 8.01726 11.3133 8.01726L11.9315 8.01653L11.932 7.60823C11.932 7.27231 12.2043 7 12.5402 7C12.8456 7 13.0984 7.22505 13.1419 7.51835L13.1485 7.60823L13.1481 8.01653H14.7383L14.7386 
            7.60823C14.7386 7.27231 15.0109 7 15.3468 7ZM9.40777 7.41055C9.71315 7.41055 9.96596 7.6356 10.0094 7.9289L10.016 8.01878V10.5173C10.016 10.8227 9.79095 11.0755 9.49765 11.119L9.40777 11.1256L7.68831 11.1255V12.2097L9.40777 12.21C9.71315 12.21 9.96596 12.4351 10.0094 12.7284L10.016 12.8183V15.2084C10.016 15.3812 9.94252 15.5458 9.81391 15.6612C9.32173 16.1027 8.42975 
            16.2805 7.08088 16.2805C6.74497 16.2805 6.47266 16.0082 6.47266 15.6723C6.47266 15.3364 6.74497 15.064 7.08088 15.064C7.86129 15.064 8.42395 14.9934 8.74634 14.8859L8.79911 14.8661V13.4263L7.08088 13.4265C6.77551 13.4265 6.52269 13.2014 6.47925 12.9081L6.47266 12.8183V10.5173C6.47266 10.212 6.69771 9.95916 6.991 9.91571L7.08088 9.90912L8.79911 9.90891V8.62646L7.08088 
            8.627C6.77551 8.627 6.52269 8.40195 6.47925 8.10866L6.47266 8.01878C6.47266 7.7134 6.69771 7.46059 6.991 7.41715L7.08088 7.41055H9.40777ZM13.1935 11.1524H11.9199L11.9207 11.8539L13.1935 11.8532V11.1524ZM15.6819 11.1524H14.4101V11.8532L15.6827 11.8539L15.6819 11.1524ZM13.1935 9.23227L11.9207 9.233L11.9199 9.93574H13.1935V9.23227ZM15.6827 9.233L14.4101 
            9.23227V9.93574H15.6819L15.6827 9.233Z" fill="#fff"></path><path d="M17.8763 16.4209H22.7763C24.5804 16.4209 26.043 17.8834 26.043 19.6876C26.043 21.4917 24.5804 22.9542 22.7763 22.9542H17.8763C16.0722 22.9542 14.6096 21.4917 14.6096 19.6876C14.6096 17.8834 16.0722 16.4209 17.8763 16.4209Z" fill="#14A3FF"></path>
            <path d="M19.9201 19.6874C19.9201 21.2661 21.1997 22.5458 22.7784 22.5458C24.3571 22.5458 25.6367 21.2661 25.6367 19.6874C25.6367 18.1088 24.3571 16.8291 22.7784 16.8291C21.1997 16.8291 19.9201 18.1088 19.9201 19.6874Z" fill="#fff"></path>
            <path d="M11.375 21H4.375C2.92525 21 1.75 19.8247 1.75 18.375V5.25C1.75 3.80025 2.92525 2.625 4.375 2.625H19.125C20.5747 2.625 21.75 3.80025 21.75 5.25V13.125" stroke="#fff" stroke-width="2" stroke-linecap="round" fill="none"></path>
            </svg>`;
            
            const danmakuOffIcon = `<svg width="28" height="28" viewBox="-2,-2,28,28">
            <path d="M15.3468 7C15.6522 7 15.905 7.22505 15.9484 7.51835L15.955 7.60823L15.9549 8.01653L16.2911 8.01726C16.5965 8.01726 16.8493 8.24231 16.8928 8.5356L16.8994 8.62548V12.463C16.8994 12.7989 16.6271 13.0713 16.2911 13.0713L14.4101 13.0707V13.7582L16.2047 13.7588C16.5406 13.7588 16.8129 14.0311 
            16.8129 14.367C16.8129 14.6724 16.5878 14.9252 16.2946 14.9687L16.2047 14.9753L14.4101 14.9748L14.4104 15.9871C14.4104 16.323 14.1381 16.5953 13.8022 16.5953C13.4968 16.5953 13.244 16.3703 13.2006 16.077L13.194 15.9871L13.1935 14.9748L11.3133 14.9753C10.9773 14.9753 10.705 14.703 10.705 14.367C10.705  
            14.0617 10.9301 13.8089 11.2234 13.7654L11.3133 13.7588L13.1935 13.7582V13.0707L11.3133 13.0713C11.0079 13.0713 10.7551 12.8462 10.7116 12.5529L10.705 12.463V8.62548C10.705 8.28957 10.9773 8.01726 11.3133 8.01726L11.9315 8.01653L11.932 7.60823C11.932 7.27231 12.2043 7 12.5402 7C12.8456 7 13.0984 7.22505 
            13.1419 7.51835L13.1485 7.60823L13.1481 8.01653H14.7383L14.7386 7.60823C14.7386 7.27231 15.0109 7 15.3468 7ZM9.40777 7.41055C9.71315 7.41055 9.96596 7.6356 10.0094 7.9289L10.016 8.01878V10.5173C10.016 10.8227 9.79095 11.0755 9.49765 11.119L9.40777 11.1256L7.68831 11.1255V12.2097L9.40777 12.21C9.71315 12.21 
            9.96596 12.4351 10.0094 12.7284L10.016 12.8183V15.2084C10.016 15.3812 9.94252 15.5458 9.81391 15.6612C9.32173 16.1027 8.42975 16.2805 7.08088 16.2805C6.74497 16.2805 6.47266 16.0082 6.47266 15.6723C6.47266 15.3364 6.74497 15.064 7.08088 15.064C7.86129 15.064 8.42395 14.9934 8.74634 14.8859L8.79911 
            14.8661V13.4263L7.08088 13.4265C6.77551 13.4265 6.52269 13.2014 6.47925 12.9081L6.47266 12.8183V10.5173C6.47266 10.212 6.69771 9.95916 6.991 9.91571L7.08088 9.90912L8.79911 9.90891V8.62646L7.08088 8.627C6.77551 8.627 6.52269 8.40195 6.47925 8.10866L6.47266 8.01878C6.47266 7.7134 6.69771 7.46059 6.991 
            7.41715L7.08088 7.41055H9.40777ZM13.1935 11.1524H11.9199L11.9207 11.8539L13.1935 11.8532V11.1524ZM15.6819 11.1524H14.4101V11.8532L15.6827 11.8539L15.6819 11.1524ZM13.1935 9.23227L11.9207 9.233L11.9199 9.93574H13.1935V9.23227ZM15.6827 9.233L14.4101 9.23227V9.93574H15.6819L15.6827 9.233Z" fill="#fff"></path>
            <path d="M17.8763 16.4209H22.7763C24.5804 16.4209 26.043 17.8834 26.043 19.6876C26.043 21.4917 24.5804 22.9542 22.7763 22.9542H17.8763C16.0722 22.9542 14.6096 21.4917 14.6096 19.6876C14.6096 17.8834 16.0722 16.4209 17.8763 16.4209Z" fill="#707070"></path>
            <path d="M15.0236 19.6874C15.0236 21.2661 16.3032 22.5458 17.8819 22.5458C19.4606 22.5458 20.7402 21.2661 20.7402 19.6874C20.7402 18.1088 19.4606 16.8291 17.8819 16.8291C16.3032 16.8291 15.0236 18.1088 15.0236 19.6874Z" fill="#fff"></path>
            <path d="M11.375 21H4.375C2.92525 21 1.75 19.8247 1.75 18.375V5.25C1.75 3.80025 2.92525 2.625 4.375 2.625H19.125C20.5747 2.625 21.75 3.80025 21.75 5.25V13.125" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round"></path></svg>`;
            
            const control = art.controls.add({
                name: 'danmaku-toggle',
                position: 'right',
                html: danmakuOnIcon,
                tooltip: '关闭弹幕',
                style: {
                    padding: '0 10px',
                },
                click: function() {
                    danmakuVisible = !danmakuVisible;
                    
                    if (danmakuManager) {
                        if (danmakuVisible) {
                            // 🔑 显示弹幕：先清理异常状态的弹幕，再启动
                            danmakuManager.clear();  // 清除所有正在显示的弹幕
                            if (art.playing) {
                                danmakuManager.unfreeze();  // 解冻
                                danmakuManager.startPlaying();  // 启动轮询
                            }
                        } else {
                            // 🔑 隐藏弹幕：先停止轮询，再冻结，最后清理
                            danmakuManager.stopPlaying();  // 停止轮询
                            danmakuManager.freeze();  // 冻结所有弹幕
                            danmakuManager.clear();  // 清除所有弹幕
                        }
                    }
                    
                    if (danmakuContainer) {
                        danmakuContainer.style.display = danmakuVisible ? 'block' : 'none';
                    }
                    
                    // 🔑 直接更新按钮的 innerHTML
                    const buttonElement = art.query('.art-control-danmaku-toggle');
                    if (buttonElement) {
                        buttonElement.innerHTML = danmakuVisible ? danmakuOnIcon : danmakuOffIcon;
                    }
                    
                    // 更新提示
                    this.tooltip = danmakuVisible ? '关闭弹幕' : '开启弹幕';
                    art.notice.show = danmakuVisible ? '弹幕已开启' : '弹幕已关闭';
                }
            });
        }

        // 添加发送弹幕输入框控制器
        function addSendControl() {
            let selectedColor = defaultColor;
            let inputElement = null;
            let colorPicker = null;
            let isLocked = false;  // 发送按钮锁定状态
            let lockTimer = null;  // 锁定计时器

            // 验证并限制 lockTime 范围 [1, 60]
            const validLockTime = Math.max(1, Math.min(60, lockTime || 5));

            // 获取 ArtPlayer 主题色，如果没有则使用默认蓝色
            const themeColor = art.option?.theme || '#0078ff';
            
            // 辅助函数：将颜色转换为 rgba 格式
            const hexToRgba = (hex, alpha = 1) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            };

            // 创建自定义控制器
            const sendControl = document.createElement('div');
            sendControl.className = 'art-control art-control-danmaku-send';
            sendControl.style.cssText = `
                display: flex;
                align-items: center;
                padding: 0 10px;
                position: relative;
                width: 100%;
            `;

            // 创建输入框容器
            const inputContainer = document.createElement('div');
            inputContainer.style.cssText = `
                display: flex;
                align-items: center;
                background: rgba(255, 255, 255, 0.15);
                border-radius: 20px;
                padding: 4px 8px;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.2);
                transition: all 0.3s;
                min-width: 200px;
                width: 100%;
            `;

            // 颜色选择器图标（左侧）
            const colorIcon = document.createElement('div');
            colorIcon.innerHTML = `
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10"/>
                </svg>
            `;
            colorIcon.style.cssText = `
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                padding: 4px;
                border-radius: 50%;
                transition: all 0.3s;
                color: ${selectedColor};
                flex-shrink: 0;
                background: rgba(0, 0, 0, 0.3);
            `;
            colorIcon.title = '选择颜色';

            // 输入框
            inputElement = document.createElement('input');
            inputElement.type = 'text';
            inputElement.placeholder = '发送弹幕...';
            inputElement.style.cssText = `
                flex: 1;
                background: transparent;
                border: none;
                outline: none;
                color: #fff;
                font-size: 13px;
                padding: 4px 8px;
                min-width: 120px;
            `;

            // 发送按钮（右侧）
            const sendButton = document.createElement('button');
            sendButton.innerHTML = `
                <svg t="1767876451079" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="6689" width="16" height="16">
                <path d="M906.4448 28.859733L75.741867 284.3648c-57.8048 17.783467-68.317867 95.061333-17.3568 127.6416l245.435733 156.893867a70.980267 70.980267 0 0 0 84.002133-5.563734l306.3296-258.491733c16.605867-14.011733 39.0144 8.379733 25.002667 25.002667L460.680533 636.228267a70.946133 70.946133 0 0 0-5.546666 83.985066l156.859733 245.4016c32.5632 50.944 109.858133 40.430933 127.6416-17.3568L995.140267 117.572267c16.7424-54.459733-34.2528-105.454933-88.695467-88.712534z" fill="#ffffff" p-id="6690"></path></svg>
            `;
            //background: ${themeColor};
            sendButton.style.cssText = `
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0.9;
                border: none;
                border-radius: 50%;
                width: 28px;
                height: 28px;
                cursor: pointer;
                transition: all 0.3s;
                color: #fff;
                flex-shrink: 0;
            `;
            sendButton.title = '发送 (Enter)';

            // 创建颜色选择器面板
            colorPicker = document.createElement('div');
            colorPicker.style.cssText = `
                position: absolute;
                bottom: 42px;
                left: 0;
                background: rgba(42, 42, 42, 0.95);
                border-radius: 8px;
                padding: 12px;
                display: none;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.1);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                z-index: 999;
            `;

            // 纯色标题
            const colorTitle = document.createElement('div');
            colorTitle.textContent = '纯色';
            colorTitle.style.cssText = `
                color: #fff;
                font-size: 12px;
                margin-bottom: 8px;
                font-weight: 500;
            `;
            colorPicker.appendChild(colorTitle);

            // 纯色网格
            const colorGrid = document.createElement('div');
            colorGrid.style.cssText = `
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 8px;
                margin-bottom: 12px;
            `;

            const colors = [
                { value: '#ffffff', name: '白色', type: 'solid' },
                { value: '#ff0000', name: '红色', type: 'solid' },
                { value: '#00ff00', name: '绿色', type: 'solid' },
                { value: '#0000ff', name: '蓝色', type: 'solid' },
                { value: '#ffff00', name: '黄色', type: 'solid' },
                { value: '#ff00ff', name: '紫色', type: 'solid' },
                { value: '#00ffff', name: '青色', type: 'solid' },
                { value: '#ffa500', name: '橙色', type: 'solid' }
            ];

            // 渐变色标题
            const gradientTitle = document.createElement('div');
            gradientTitle.textContent = '渐变';
            gradientTitle.style.cssText = `
                color: #fff;
                font-size: 12px;
                margin-bottom: 8px;
                font-weight: 500;
            `;

            // 渐变色网格
            const gradientGrid = document.createElement('div');
            gradientGrid.style.cssText = `
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 8px;
            `;

            const gradients = [
                { value: 'linear-gradient(90deg, #ff0000, #ff7f00)', name: '红橙渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #00ff00, #0000ff)', name: '绿蓝渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #ff00ff, #00ffff)', name: '紫青渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #ffd700, #ff69b4)', name: '金粉渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #ff0000, #ffff00, #00ff00)', name: '彩虹渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #00d4ff, #0066ff)', name: '冰蓝渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #ff6b6b, #ffd93d)', name: '日落渐变', type: 'gradient' },
                { value: 'linear-gradient(90deg, #a8edea, #fed6e3)', name: '梦幻渐变', type: 'gradient' }
            ];

            // 创建颜色/渐变按钮的通用函数
            const createColorButton = (colorOption, gridElement) => {
                const colorBtn = document.createElement('button');
                colorBtn.style.cssText = `
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 2px solid ${selectedColor === colorOption.value ? themeColor : 'rgba(255, 255, 255, 0.2)'};
                    background: ${colorOption.value};
                    cursor: pointer;
                    transition: all 0.2s;
                    box-shadow: ${selectedColor === colorOption.value ? `0 0 0 2px ${hexToRgba(themeColor, 0.3)}` : 'none'};
                `;
                colorBtn.title = colorOption.name;
                colorBtn.dataset.colorValue = colorOption.value;
                colorBtn.dataset.colorType = colorOption.type;

                colorBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    selectedColor = colorOption.value;
                    
                    // 更新所有按钮状态（包括纯色和渐变）
                    colorGrid.querySelectorAll('button').forEach(btn => {
                        btn.style.border = '2px solid rgba(255, 255, 255, 0.2)';
                        btn.style.boxShadow = 'none';
                    });
                    gradientGrid.querySelectorAll('button').forEach(btn => {
                        btn.style.border = '2px solid rgba(255, 255, 255, 0.2)';
                        btn.style.boxShadow = 'none';
                    });
                    
                    colorBtn.style.border = `2px solid ${themeColor}`;
                    colorBtn.style.boxShadow = `0 0 0 2px ${hexToRgba(themeColor, 0.3)}`;
                    
                    // 更新颜色图标
                    if (colorOption.type === 'gradient') {
                        // 渐变色：使用渐变背景
                        colorIcon.style.background = colorOption.value;
                        colorIcon.style.color = 'transparent';
                        const svg = colorIcon.querySelector('svg');
                        if (svg) {
                            svg.style.fill = 'url(#gradient)';
                        }
                    } else {
                        // 纯色：使用单色填充
                        colorIcon.style.background = 'rgba(0, 0, 0, 0.3)';
                        colorIcon.style.color = colorOption.value;
                        const svg = colorIcon.querySelector('svg');
                        if (svg) {
                            svg.style.fill = colorOption.value;
                        }
                    }
                });

                colorBtn.addEventListener('mouseenter', () => {
                    if (selectedColor !== colorOption.value) {
                        colorBtn.style.transform = 'scale(1.1)';
                    }
                });

                colorBtn.addEventListener('mouseleave', () => {
                    colorBtn.style.transform = 'scale(1)';
                });

                gridElement.appendChild(colorBtn);
            };

            // 创建纯色按钮
            colors.forEach(color => createColorButton(color, colorGrid));

            // 创建渐变色按钮
            gradients.forEach(gradient => createColorButton(gradient, gradientGrid));

            // 添加到颜色选择器面板
            colorPicker.appendChild(colorGrid);
            colorPicker.appendChild(gradientTitle);
            colorPicker.appendChild(gradientGrid);

            // 鼠标悬停显示颜色选择器
            colorIcon.addEventListener('mouseenter', () => {
                colorPicker.style.display = 'block';
            });

            colorIcon.addEventListener('click', (e) => {
                e.stopPropagation();
                colorPicker.style.display = colorPicker.style.display === 'none' ? 'block' : 'none';
            });

            // 鼠标离开隐藏颜色选择器
            sendControl.addEventListener('mouseleave', () => {
                setTimeout(() => {
                    colorPicker.style.display = 'none';
                }, 300);
            });

            // 锁定发送按钮
            const lockSendButton = () => {
                isLocked = true;
                let remainingTime = validLockTime;
                const originalHTML = sendButton.innerHTML;

                // 禁用按钮
                sendButton.disabled = true;
                sendButton.style.cursor = 'not-allowed';
                sendButton.style.opacity = '0.5';

                // 显示倒计时
                const updateCountdown = () => {
                    sendButton.innerHTML = `<span style="font-size: 12px;">${remainingTime}s</span>`;
                    remainingTime--;

                    if (remainingTime < 0) {
                        // 解锁按钮
                        clearInterval(lockTimer);
                        isLocked = false;
                        sendButton.disabled = false;
                        sendButton.style.cursor = 'pointer';
                        sendButton.style.opacity = '0.9';
                        sendButton.innerHTML = originalHTML;
                    }
                };

                updateCountdown();
                lockTimer = setInterval(updateCountdown, 1000);
            };

            // 发送弹幕功能
            const sendDanmaku = () => {
                // 检查是否锁定
                if (isLocked) {
                    art.notice.show = '发送太频繁，请稍后再试';
                    return;
                }

                const text = inputElement.value.trim();
                if (!text) {
                    art.notice.show = '请输入弹幕内容';
                    return;
                }

                // 发送弹幕
                send(text, {
                    color: selectedColor,
                    time: art.currentTime
                });

                // 清空输入框
                inputElement.value = '';
                art.notice.show = '弹幕已发送';

                // 锁定发送按钮
                lockSendButton();
            };

            // 发送按钮点击事件
            sendButton.addEventListener('click', (e) => {
                e.stopPropagation();
                if (!isLocked) {
                    sendDanmaku();
                }
            });

            // 发送按钮悬停效果
            sendButton.addEventListener('mouseenter', () => {
                if (!isLocked) {
                    sendButton.style.opacity = '1';
                    sendButton.style.transform = 'scale(1.1)';
                }
            });

            sendButton.addEventListener('mouseleave', () => {
                if (!isLocked) {
                    sendButton.style.opacity = '0.9';
                    sendButton.style.transform = 'scale(1)';
                }
            });

            // 回车发送
            inputElement.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' && !isLocked) {
                    sendDanmaku();
                }
            });

            // 输入框聚焦效果（使用主题色）
            inputElement.addEventListener('focus', () => {
                inputContainer.style.background = 'rgba(255, 255, 255, 0.15)';
                inputContainer.style.borderColor = hexToRgba(themeColor, 0.5);
            });

            inputElement.addEventListener('blur', () => {
                inputContainer.style.background = 'rgba(255, 255, 255, 0.1)';
                inputContainer.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            });

            // 组装控制器
            inputContainer.appendChild(colorIcon);
            inputContainer.appendChild(inputElement);
            inputContainer.appendChild(sendButton);
            sendControl.appendChild(inputContainer);
            sendControl.appendChild(colorPicker);

            // 添加到控制栏中间位置
            const controls = art.template.$controls;
            
            // 获取中间容器（ArtPlayer 默认有，但是 display: none）
            let controlsCenter = controls.querySelector('.art-controls-center');
            
            if (controlsCenter) {
                // 显示中间容器
                controlsCenter.style.display = 'flex';
                controlsCenter.style.alignItems = 'center';
                controlsCenter.style.justifyContent = 'center';
                controlsCenter.style.flex = '1';
                controlsCenter.style.padding = '0 10px';
                
                controlsCenter.appendChild(sendControl);
            } else {
                // 降级方案：如果找不到中间容器，添加到右侧
                const controlsRight = art.template.$controlsRight;
                if (controlsRight) {
                    controlsRight.appendChild(sendControl);
                }
            }
        }

        /**
         * 发送弹幕
         * @param {string} text - 弹幕文本内容
         * @param {Object} config - 弹幕配置
         * @param {string} config.color - 弹幕颜色（默认使用 defaultColor）
         * @param {string} config.fontSize - 字体大小（默认使用 fontSize）
         * @param {number} config.time - 弹幕时间戳，用于同步视频进度（默认使用当前播放时间）
         */
        function send(text, config = {}) {
            if (!danmakuManager) {
                console.warn('[弹幕插件] 弹幕管理器未初始化');
                return;
            }

            const danmakuData = {
                text: text,
                color: config.color || defaultColor,
                fontSize: config.fontSize || fontSize,
                time: config.time !== undefined ? config.time : art.currentTime,  // 支持 time=0
            };

            console.log('[弹幕插件] 发送弹幕:', danmakuData);

            // 立即显示弹幕
            danmakuManager.unshift(danmakuData);

            // 执行发送回调
            if (onSend && typeof onSend === 'function') {
                onSend(danmakuData);
            }
        }

        // 加载弹幕列表到调度器队列
        function load(danmakuList = []) {
            // 🔑 即使弹幕管理器未初始化，也先缓存数据
            // 清空现有队列
            danmakuQueue = [];
            currentDanmakuIndex = 0;  // 🔑 重置游标

            // 将弹幕添加到调度器队列（带 time 字段）
            danmakuQueue = danmakuList.map(item => ({
                text: item.text,
                time: item.time || 0,  // 时间（秒）
                color: item.color || defaultColor,
                fontSize: item.fontSize || fontSize
            }));

            // 按时间排序（如果还没排序）
            danmakuQueue.sort((a, b) => a.time - b.time);

            console.log('[弹幕插件] 加载弹幕到队列:', danmakuQueue.length, '条');
            
            // 🔑 如果弹幕管理器已初始化，清空显示中的弹幕
            if (danmakuManager) {
                danmakuManager.clear();
                
                // 🔑 如果视频不是从头播放，移动游标到当前时间
                if (art.currentTime > 0) {
                    currentDanmakuIndex = findDanmakuIndexByTime(art.currentTime);
                    console.log(`[弹幕调度器] 初始游标设置为: ${currentDanmakuIndex}（当前时间: ${art.currentTime.toFixed(2)}s）`);
                }
                
                // 如果视频正在播放，启动调度器
                if (art.playing) {
                    startScheduler();
                }
            } else {
                console.log('[弹幕插件] 弹幕管理器未初始化，数据已缓存，等待容器准备完成');
            }
        }

        // 清空弹幕
        function clear() {
            if (danmakuManager) {
                danmakuManager.clear();
                console.log('[弹幕插件] 已清空弹幕');
            }
        }

        // 显示/隐藏弹幕
        function toggle(show) {
            danmakuVisible = typeof show === 'boolean' ? show : !danmakuVisible;
            
            if (danmakuContainer) {
                danmakuContainer.style.display = danmakuVisible ? 'block' : 'none';
            }
            
            if (danmakuManager) {
                if (danmakuVisible) {
                    danmakuManager.startPlaying();
                } else {
                    danmakuManager.stopPlaying();
                }
            }
        }

        // 设置透明度
        function setOpacity(value) {
            if (danmakuContainer) {
                danmakuContainer.style.opacity = value;
            }
        }

        // 销毁弹幕系统
        function destroy() {
            stopScheduler();  // 停止调度器
            
            if (danmakuManager) {
                danmakuManager.destroy();
                danmakuManager = null;
            }
            
            danmakuQueue = [];  // 清空队列
            currentDanmakuIndex = 0;  // 🔑 重置游标
            isSchedulerRunning = false;  // 🔑 重置状态
            isSeeking = false;  // 🔑 重置状态
            isInitialized = false;
            console.log('[弹幕插件] 已销毁');
        }

        // 初始化插件
        init();

        // 返回插件 API
        return {
            name: 'artplayerPluginDanmaku',
            // 发送弹幕
            send,
            // 加载弹幕列表
            load,
            // 清空弹幕
            clear,
            // 显示/隐藏弹幕
            toggle,
            // 设置透明度
            setOpacity,
            // 销毁
            destroy,
            // 获取弹幕管理器实例
            get manager() {
                return danmakuManager;
            },
            // 获取容器元素
            get container() {
                return danmakuContainer;
            },
        };
    };
}

